/**
 * Automation send conditions, reduced to something a partner can read.
 *
 * An automation step is a wait followed by a condition check: Mailcoach's
 * `BigConditionSectionAction` walks `automation_sections.conditions` in order,
 * and a subscriber only receives the mail when **every** entry passes. A failing
 * entry does not skip the step — it takes the subscriber out of the automation
 * — which is why the UI says "only if" rather than "otherwise skipped".
 *
 * The stored shape is the raw Mailcoach one (`{type, attribute, comparison,
 * value}`), so this module is the single place that interprets it. It stops at
 * *structure*: the operator comes back as a stable slug rather than a word,
 * because the words are translated and this stays pure and testable.
 */
import {
  AutomationComparison,
  AutomationConditionModel,
} from '@/typings/api/emailCenter';

/**
 * Operator slugs — the comparison, or the tag checks folded in as operators of
 * their own so a condition always reads subject-operator-value.
 *
 * Slugs rather than the raw `=` / `>=` symbols because they end up in message
 * ids, and an id containing `=` is one nobody can grep for.
 */
export type ConditionOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'in'
  | 'not_in'
  | 'is_empty'
  | 'is_not_empty'
  | 'has_tag'
  | 'has_not_tag';

/** A condition split into the parts a chip renders as separate spans. */
export interface ReadableCondition {
  /**
   * What is tested: the attribute's label, or `null` for the tag checks — those
   * name their subject in the operator ("has tag"), so a chip would otherwise
   * repeat the word.
   */
  subject: string | null;
  operator: ConditionOperator;
  /**
   * What it is tested against, already split for the list comparisons. Empty
   * when the operator takes no value (`is_empty` / `is_not_empty`).
   */
  values: string[];
}

const COMPARISON_SLUGS: Record<AutomationComparison, ConditionOperator> = {
  '=': 'eq',
  '!=': 'neq',
  '>': 'gt',
  '<': 'lt',
  '>=': 'gte',
  '<=': 'lte',
  in: 'in',
  not_in: 'not_in',
  is_empty: 'is_empty',
  is_not_empty: 'is_not_empty',
};

/** Operators Mailcoach evaluates without looking at `value`. */
const VALUELESS: ConditionOperator[] = ['is_empty', 'is_not_empty'];

/** Operators whose `value` is one comma-separated list, not a single term. */
const LIST_OPERATORS: ConditionOperator[] = ['in', 'not_in'];

/**
 * The journey-scoped prefix cake gives every subscriber meta attribute
 * (`MailcoachService::getMetaAttributeName`: `ServiceRequest_<journeyId>_meta_`).
 * It identifies the attribute inside Mailcoach and says nothing to a reader, so
 * the label drops it — the journey is already the page's scope.
 */
const META_ATTRIBUTE_PREFIX = /^ServiceRequest_\d+_meta_/;

/**
 * `ServiceRequest_42_meta_zip` → `zip`, `Service_start` → `Service start`.
 *
 * Deliberately no capitalisation beyond what the stored key has: these are the
 * partner's own form fields, and "Zip" for a field they named `zip` is a change,
 * not a fix.
 */
export function humanizeAttribute(attribute: string): string {
  return attribute.replace(META_ATTRIBUTE_PREFIX, '').replace(/_/g, ' ').trim();
}

/** `'Berlin, Hamburg'` → `['Berlin', 'Hamburg']`, the way Mailcoach splits it. */
function splitList(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/** `value` is `unknown` on the wire — whatever JSON the section was saved with. */
function stringifyValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (Array.isArray(value)) return value.map(stringifyValue).join(', ');
  if (typeof value === 'object') return '';
  return String(value);
}

/**
 * One stored condition as its readable parts, or `null` when it carries nothing
 * to say — a tag check with no tag, or an `attribute` row with no attribute.
 * Both are half-written rules that Mailcoach cannot match on either.
 *
 * An unknown comparison falls back to `eq` rather than dropping the row: a rule
 * the reader cannot parse is still a rule that runs, and hiding it would
 * misreport when the mail goes out.
 */
export function describeCondition(
  condition: AutomationConditionModel,
): ReadableCondition | null {
  const value = stringifyValue(condition.value);

  if (condition.type === 'has_tag' || condition.type === 'has_not_tag') {
    if (!value) return null;
    return { subject: null, operator: condition.type, values: [value] };
  }

  if (!condition.attribute) return null;

  const operator = condition.comparison
    ? COMPARISON_SLUGS[condition.comparison] ?? 'eq'
    : 'eq';
  const subject = humanizeAttribute(condition.attribute);

  if (VALUELESS.includes(operator)) return { subject, operator, values: [] };

  return {
    subject,
    operator,
    values: LIST_OPERATORS.includes(operator)
      ? splitList(value)
      : [value].filter(Boolean),
  };
}

/** Every condition of a step that has something to say, in evaluation order. */
export function describeConditions(
  conditions: AutomationConditionModel[] | null | undefined,
): ReadableCondition[] {
  return (conditions ?? [])
    .map(describeCondition)
    .filter((described): described is ReadableCondition => described !== null);
}
